import { useMemo, useReducer } from 'react';

import { useNestedLocationStore } from '../../../newStore/nestedLocationStore';
import {
  changeAncestorsSelectionStatus,
  checkAndUpdateAllLocationsSelected,
  clearChildrenSelectStatus,
  clearLocationSubtree,
  convertSelectedLocationsToMap,
  getLocationAncestors,
  getSelectedAncestor,
  getTopLevelAncestor,
  handleExcludeLocationFromAllSelected,
  LocationsIndexes,
  transitionGroupSelectionToPartial,
  unselectLocationAncestors,
} from '../utils/LocationsHelpers';

/**
 * The status of a location can be:
 * - selected: the location is selected and all its children are selected
 * - excluded: the location is excluded and all its children are excluded
 * - onlyChildren: the location is not selected but all its children are selected by default
 * - partialChildren: group mode — parent kept after "deselect all" children (indeterminate UI)
 * - undefined: the location is not explicitly selected or excluded, but it can be selected by default if all its ancestors are selected
 *
 * Useful to manage multiple selections.
 */
export type SelectedStatus =
  | 'selected'
  | 'excluded'
  | 'onlyChildren'
  | 'partialChildren'
  | undefined;
export type SelectedLocations = Map<number, SelectedStatus>;
export type LocationType = 'NestableLocation' | 'Location';

export interface LocationsState {
  selectedLocations: SelectedLocations;
  allLocationsSelected: boolean;
  legacySelectedLocations: number[];
  autoSelectChildren: boolean;
  groupSelectionMode: boolean;
}

interface UpdateLocationSelectionAction {
  type: 'update_location_selection';
  payload: {
    locationId: number | undefined;
    locationType: LocationType;
    multipleSelection?: boolean;
    onlyChildrenAllowed?: boolean;
  };
}

interface ToggleAllLocationsSelectionAction {
  type: 'toggle_all_locations_selection';
}

interface ResetLocationsStateAction {
  type: 'reset_locations_state';
  payload?: {
    selectedLocations?: Record<string | number, SelectedStatus>;
    allLocationsSelected?: boolean;
    legacySelectedLocations?: number[];
  };
}

export type ToggleAllChildrenMode = 'selectAll' | 'deselectAll';

interface ToggleAllChildrenAction {
  type: 'toggle_all_children';
  payload: {
    parentLocationId: number;
    mode: ToggleAllChildrenMode;
  };
}

export type LocationsAction =
  | UpdateLocationSelectionAction
  | ToggleAllLocationsSelectionAction
  | ResetLocationsStateAction
  | ToggleAllChildrenAction;

interface LocationsActionMetadata {
  locationsIndex: LocationsIndexes;
}

type LocationsActionWithMetadata = LocationsAction & { metadata: LocationsActionMetadata };

const getDefaultLocationsState = (
  initialSelectedLocations?: Record<string, SelectedStatus>,
  initialAllLocationsSelected?: boolean,
  initialLegacySelectedLocations?: number[],
  initialAutoSelectChildren?: boolean,
  initialGroupSelectionMode?: boolean,
): LocationsState => ({
  selectedLocations: convertSelectedLocationsToMap(initialSelectedLocations ?? {}),
  allLocationsSelected: initialAllLocationsSelected ?? false,
  legacySelectedLocations: initialLegacySelectedLocations ?? [],
  autoSelectChildren: initialAutoSelectChildren ?? true,
  groupSelectionMode: initialGroupSelectionMode ?? false,
});

const createStateSnapshot = (state: LocationsState): LocationsState => {
  return {
    ...state,
    selectedLocations: new Map(state.selectedLocations),
    legacySelectedLocations: [...state.legacySelectedLocations],
  };
};

/**
 * Group-selection-mode toggle (parent checkbox):
 * - Both root and non-root parents: off ↔ full selected. Clicking a fully selected (or
 *   partially selected) parent clears its subtree — clicking a checkbox that looks "checked"
 *   should always uncheck it.
 * - The `partialChildren` state is only reachable indirectly: tapping a child under a fully
 *   selected ancestor moves the top ancestor to `partialChildren` (parent stays in play; children
 *   are picked one by one), or via the "Deseleccionar todos" shortcut handled by
 *   `toggle_all_children`.
 */
const toggleGroupSelectionAction = (
  state: LocationsState,
  locationId: number,
  { locationsIndex: { locationById, childrenByParentId } }: LocationsActionMetadata,
) => {
  const newState = createStateSnapshot(state);
  const location = locationById.get(locationId);
  if (!location) return state;

  const selectedAncestor = getSelectedAncestor(
    location.id,
    newState.selectedLocations,
    locationById,
  );

  if (selectedAncestor) {
    const topLevelAncestor = getTopLevelAncestor(location.id, locationById);
    if (topLevelAncestor) {
      transitionGroupSelectionToPartial(
        topLevelAncestor.id,
        locationById,
        childrenByParentId,
        newState.selectedLocations,
      );
    }
    return newState;
  }

  const currentStatus = newState.selectedLocations.get(location.id);

  if (currentStatus === 'selected' || currentStatus === 'partialChildren') {
    clearLocationSubtree(
      location.id,
      locationById,
      childrenByParentId,
      newState.selectedLocations,
    );
    return newState;
  }

  clearLocationSubtree(
    location.id,
    locationById,
    childrenByParentId,
    newState.selectedLocations,
  );
  newState.selectedLocations.set(location.id, 'selected');

  return newState;
};

const applyToggleAllChildren = (
  state: LocationsState,
  parentLocationId: number,
  mode: ToggleAllChildrenMode,
  { locationsIndex: { locationById, childrenByParentId } }: LocationsActionMetadata,
): LocationsState => {
  if (!state.groupSelectionMode) return state;
  const newState = createStateSnapshot(state);
  const parentLocation = locationById.get(parentLocationId);
  if (!parentLocation) return state;

  if (mode === 'deselectAll') {
    if (newState.selectedLocations.get(parentLocationId) === 'selected') {
      transitionGroupSelectionToPartial(
        parentLocationId,
        locationById,
        childrenByParentId,
        newState.selectedLocations,
      );
    }
    return newState;
  }

  if (mode === 'selectAll') {
    if (newState.selectedLocations.get(parentLocationId) === 'partialChildren') {
      clearChildrenSelectStatus(
        parentLocationId,
        locationById,
        childrenByParentId,
        newState.selectedLocations,
      );
      newState.selectedLocations.set(parentLocationId, 'selected');
    }
    return newState;
  }

  return newState;
};

/**
 * Toggles the selection status of a specific location
 * At the same time it change the selection status of the location and its ancestors or parents.
 * It also clear the selection status of all children of the location.
 */
export const toggleMultipleLocationSelectionAction = (
  state: LocationsState,
  locationId: number,
  metadata: LocationsActionMetadata,
) => {
  if (state.groupSelectionMode) {
    return toggleGroupSelectionAction(state, locationId, metadata);
  }

  const { locationsIndex: { locationById, childrenByParentId } } = metadata;
  const newState = createStateSnapshot(state);

  const location = locationById.get(locationId);
  if (!location) return state;

  if (!state.autoSelectChildren) {
    if (newState.selectedLocations.get(location.id) === 'selected') {
      newState.selectedLocations.delete(location.id);
    } else {
      newState.selectedLocations.set(location.id, 'selected');
    }
    return newState;
  }

  const ancestors = getLocationAncestors(location.id, locationById);
  const locationSelectedStatus = newState.selectedLocations.get(location.id);

  const atLeastOneAncestorSelected = ancestors.some((ancestor) =>
    ['selected', 'onlyChildren'].includes(newState.selectedLocations.get(ancestor.id)),
  );
  const noAncestorExcluded = ancestors.every(
    (ancestor) => newState.selectedLocations.get(ancestor.id) !== 'excluded',
  );

  const isSelected =
    (!['excluded', 'onlyChildren'].includes(locationSelectedStatus) &&
      atLeastOneAncestorSelected &&
      noAncestorExcluded) ||
    locationSelectedStatus === 'selected' ||
    state.allLocationsSelected;

  clearChildrenSelectStatus(
    location.id,
    locationById,
    childrenByParentId,
    newState.selectedLocations,
  );

  if (isSelected) {
    newState.selectedLocations.set(location.id, 'excluded');
    unselectLocationAncestors(location.id, newState.selectedLocations, locationById);
    if (newState.allLocationsSelected) {
      handleExcludeLocationFromAllSelected(
        location,
        newState.selectedLocations,
        locationById,
        childrenByParentId,
      );
    }
    newState.allLocationsSelected = false;
  } else {
    newState.selectedLocations.set(location.id, 'selected');
    changeAncestorsSelectionStatus(
      location.parent_id,
      newState.selectedLocations,
      locationById,
      childrenByParentId,
    );
    newState.allLocationsSelected = checkAndUpdateAllLocationsSelected(
      newState.selectedLocations,
      locationById,
    );
  }
  return newState;
};

const toggleMultipleLegacyLocationSelectionAction = (state: LocationsState, locationId: number) => {
  const newState = createStateSnapshot(state);
  const isCurrentlySelected = newState.legacySelectedLocations.includes(locationId);
  if (isCurrentlySelected) {
    newState.legacySelectedLocations = newState.legacySelectedLocations.filter(
      (id) => id !== locationId,
    );
  } else {
    newState.legacySelectedLocations.push(locationId);
  }
  return newState;
};

const toggleAllLocationsSelectionAction = (state: LocationsState) => {
  const newState = createStateSnapshot(state);
  if (!newState.allLocationsSelected) {
    newState.selectedLocations.clear();
  }
  newState.allLocationsSelected = !newState.allLocationsSelected;
  return newState;
};

const resetLocationsStateAction = (
  state: LocationsState,
  payload?: ResetLocationsStateAction['payload'],
): LocationsState => {
  const newState = createStateSnapshot(state);
  const { selectedLocations, legacySelectedLocations, allLocationsSelected } = payload || {};
  if (selectedLocations) {
    newState.selectedLocations = convertSelectedLocationsToMap(selectedLocations);
  }
  if (legacySelectedLocations) {
    newState.legacySelectedLocations = legacySelectedLocations;
  }
  if (allLocationsSelected !== undefined) {
    newState.allLocationsSelected = allLocationsSelected;
  }
  return newState;
};

const toggleSingleLocationSelectionAction = (state: LocationsState, locationId: number) => {
  const newState = createStateSnapshot(state);
  const isCurrentlySelected = newState.selectedLocations.get(locationId) === 'selected';
  newState.selectedLocations.clear();
  if (!isCurrentlySelected) {
    newState.selectedLocations.set(locationId, 'selected');
  }
  newState.legacySelectedLocations = [];
  return newState;
};

const toggleSingleLegacyLocationSelectionAction = (state: LocationsState, locationId: number) => {
  const newState = createStateSnapshot(state);
  const isCurrentlySelected = newState.legacySelectedLocations.includes(locationId);
  if (isCurrentlySelected) {
    newState.legacySelectedLocations = [];
  } else {
    newState.legacySelectedLocations = [locationId];
  }
  newState.selectedLocations.clear();
  return newState;
};

const locationsReducer = (state: LocationsState, action: LocationsActionWithMetadata) => {
  switch (action.type) {
    case 'update_location_selection': {
      if (!action.payload.locationId) return state;

      if (action.payload.locationType === 'NestableLocation') {
        if (action.payload.multipleSelection) {
          return toggleMultipleLocationSelectionAction(
            state,
            action.payload.locationId,
            action.metadata,
          );
        } else {
          return toggleSingleLocationSelectionAction(state, action.payload.locationId);
        }
      } else {
        if (action.payload.multipleSelection) {
          return toggleMultipleLegacyLocationSelectionAction(state, action.payload.locationId);
        } else {
          return toggleSingleLegacyLocationSelectionAction(state, action.payload.locationId);
        }
      }
    }
    case 'toggle_all_locations_selection': {
      return toggleAllLocationsSelectionAction(state);
    }
    case 'reset_locations_state': {
      return resetLocationsStateAction(state, action.payload);
    }
    case 'toggle_all_children': {
      return applyToggleAllChildren(
        state,
        action.payload.parentLocationId,
        action.payload.mode,
        action.metadata,
      );
    }
    default:
      return state;
  }
};

type MultipleSelectionProps = {
  multipleSelection: true;
  autoSelectChildren?: boolean;
  /**
   * When true, parent selection collapses to a single 'selected' entry (children are not
   * enumerated in state). Deselecting a descendant under a fully selected ancestor moves the
   * ancestor to `partialChildren` so the parent stays selected while children are picked
   * individually. Requires `autoSelectChildren: true` so children render visually as selected.
   */
  groupSelectionMode?: boolean;
};

type SingleSelectionProps = {
  multipleSelection?: false;
  autoSelectChildren?: never;
  groupSelectionMode?: never;
};

type SelectionProps = MultipleSelectionProps | SingleSelectionProps;

type UseLocationsSelectorStateProps = {
  initialSelectedLocations?: Record<string | number, SelectedStatus>;
  initialAllLocationsSelected?: boolean;
  initialLegacySelectedLocations?: number[];
} & SelectionProps;

/**
 * Derive the one-time initial selection maps for {@link useLocationsSelectorState}
 * from a single `{ id, type }` location value. Shared by the planning wizard
 * (Step1_Details) and the task editor (TaskInfoContainer) so the
 * NestableLocation-vs-legacy seeding lives in one place instead of being copied.
 */
export function deriveLocationSelectorInit(value: { id: number; type: LocationType } | null | undefined): {
  initialSelectedLocations: Record<string | number, SelectedStatus> | undefined;
  initialLegacySelectedLocations: number[] | undefined;
} {
  if (!value) {
    return { initialSelectedLocations: undefined, initialLegacySelectedLocations: undefined };
  }
  return {
    initialSelectedLocations:
      value.type === 'NestableLocation' ? { [String(value.id)]: 'selected' } : undefined,
    initialLegacySelectedLocations: value.type === 'Location' ? [value.id] : undefined,
  };
}

/**
 * Reducer to manage the state of the complex state of the locations selector.
 * @param initialSelectedLocations - Default {}, only used when the component is mounted.
 * @param initialAllLocationsSelected - Default false, only used when the component is mounted.
 * @param initialLegacySelectedLocations - Default [], only used when the component is mounted.
 * @param multipleSelection - Default false, whether the multiple selection is enabled.
 * @param autoSelectChildren - Only available if multipleSelection is true. If true, when a location is selected, all its children are selected.
 * @returns The locations selector state and the functions to manage the state.
 */
export const useLocationsSelectorState = (config?: UseLocationsSelectorStateProps) => {
  const {
    initialSelectedLocations = {},
    initialAllLocationsSelected = false,
    initialLegacySelectedLocations = [],
    multipleSelection = false,
    autoSelectChildren = true,
    groupSelectionMode = false,
  } = config ?? {};
  const { locationsIndex } = useNestedLocationStore();

  const [locationsState, dispatch] = useReducer(
    locationsReducer,
    getDefaultLocationsState(
      initialSelectedLocations,
      initialAllLocationsSelected,
      initialLegacySelectedLocations,
      autoSelectChildren,
      groupSelectionMode,
    ),
  );

  const handleChangeLocationSelection = (locationId: number, locationType: LocationType) => {
    dispatch({
      type: 'update_location_selection',
      payload: { locationId, locationType, multipleSelection },
      metadata: { locationsIndex },
    });
  };

  const handleChangeAllLocationsSelection = () => {
    dispatch({
      type: 'toggle_all_locations_selection',
      metadata: { locationsIndex },
    });
  };

  const handleResetLocationsState = (payload?: ResetLocationsStateAction['payload']) => {
    dispatch({
      type: 'reset_locations_state',
      payload,
      metadata: { locationsIndex },
    });
  };

  const handleToggleAllChildren = (parentLocationId: number, mode: ToggleAllChildrenMode) => {
    dispatch({
      type: 'toggle_all_children',
      payload: { parentLocationId, mode },
      metadata: { locationsIndex },
    });
  };

  const selectedLocation: { id: number; type: LocationType } | undefined = useMemo(() => {
    if (!multipleSelection) {
      if (locationsState.selectedLocations.size > 0) {
        return {
          id: Array.from(locationsState.selectedLocations.keys())[0],
          type: 'NestableLocation',
        };
      }
      if (locationsState.legacySelectedLocations.length > 0) {
        return { id: locationsState.legacySelectedLocations[0], type: 'Location' };
      }
    }
    return undefined;
  }, [locationsState.selectedLocations, locationsState.legacySelectedLocations, multipleSelection]);

  return {
    locationsState,
    handleChangeLocationSelection,
    handleChangeAllLocationsSelection,
    handleResetLocationsState,
    handleToggleAllChildren,
    selectedLocation,
  };
};
